import { useWatch } from 'react-hook-form';
import { useFieldContext } from '../FieldContext';
import { FieldControlBase } from './FieldControlBase';
import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type { FieldFrameSpec, FieldPartProps } from './fieldComponent.types';
import { RateEditor } from './rate/RateEditor';
import {
  CustomRateSummary,
  RateCardSummary,
  RateCardTerms
} from './rate/RateSummary';

/**
 * One rate (`RateFormValue`) of the rates list.
 *
 * - display: a summary — billing-unit icon, "Day rate", "CAD 850 / day",
 *   fee chips ("+15% agency") and the note;
 * - edit: `RateEditor` (name, currency | amount | billing unit, fees in
 *   booking order, note, and the cost per unit with fees);
 * - a rate-card row (`rate_id` set) is read-only: the card's summary in
 *   display; in edit its row header names it (`RateRowHeading`) and the field
 *   shows the rest and "Detach and edit", which turns the row into a custom
 *   rate holding the card's values. Its own columns stay in the form
 *   untouched and are ignored on write.
 *
 * The field is its own size container, so the layout follows the width it
 * is given (drawer, pane or create dialog), not the viewport.
 */
export function RateField({
  spec,
  path,
  hideLabel
}: FieldPartProps<
  FieldFrameSpec &
    Pick<FieldSpecOf<'rate'>, 'currencies' | 'defaultCurrency' | 'rateCards'>
>) {
  const ctx = useFieldContext();
  // Not bound to an input: read straight from the unit form values.
  const rateId: number | null = useWatch({ name: `${path}.rate_id` }) ?? null;

  return (
    <FieldControlBase spec={spec} hideLabel={hideLabel} path={path}>
      <div className="@container min-w-0">
        {rateId !== null && ctx.mode === 'edit' ? (
          <RateCardTerms
            card={spec.rateCards.find(option => option.id === rateId) ?? null}
            path={path}
          />
        ) : rateId !== null ? (
          <RateCardSummary
            rateId={rateId}
            card={spec.rateCards.find(option => option.id === rateId) ?? null}
          />
        ) : ctx.mode === 'display' ? (
          <CustomRateSummary path={path} />
        ) : (
          <RateEditor spec={spec} path={path} />
        )}
      </div>
    </FieldControlBase>
  );
}
